import React, { useState, useEffect, useContext, useMemo } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Copy,
  Pencil,
  Plus,
  Route,
  Trash2,
  Wrench,
} from "lucide-react";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppTooltip from "../../components/AppTooltip";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";

import FlowBuilderModal from "../../components/FlowBuilderModal";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";

  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      boxSizing: "border-box",
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark || theme.palette.primary.main },
    },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
    },
    flowName: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    statusActive: {
      background: isDark ? "rgba(76,175,80,0.15)" : "rgba(76,175,80,0.12)",
      color: isDark ? "#81c784" : "#2e7d32",
    },
    statusInactive: {
      background: isDark ? "rgba(229,57,53,0.18)" : "rgba(229,57,53,0.10)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    statusCell: {
      width: 180,
      minWidth: 180,
    },
    actionsCell: {
      width: 190,
      minWidth: 190,
      whiteSpace: "nowrap",
      paddingRight: "16px !important",
      "& > *": {
        marginLeft: 4,
      },
      "& > *:first-child": {
        marginLeft: 0,
      },
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: isDark ? "#666" : "#999",
      fontSize: 13.5,
    },
  };
});

const FlowBuilder = () => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketSearchParam, clear } = useSearch();

  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);

  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [reloadData]);

  useEffect(() => {
    clear();
    return () => clear();
  }, [clear]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        setReloadData(old => !old);
      }

      if (data.action === "delete") {
        setReloadData(old => !old);
      }
    };

    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.off(`company-${companyId}-contact`, onContact);
    };
  }, [socket, user?.companyId]);

  const filteredWebhooks = useMemo(() => {
    const term = (ticketSearchParam || "").trim().toLowerCase();
    if (!term) return webhooks;
    return webhooks.filter((flow) =>
      String(flow.name || "").toLowerCase().includes(term) ||
      String(flow.id || "").includes(term) ||
      (flow.active ? i18n.t("flowbuilder.status.active") : i18n.t("flowbuilder.status.inactive"))
        .toLowerCase()
        .includes(term)
    );
  }, [webhooks, ticketSearchParam]);

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setSelectedWebhookName(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setSelectedWebhookName(null);
    setContactModalOpen(false);
  };

  const handleDeleteWebhook = async webhookId => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`).then(res => {
        setDeletingContact(null);
        setReloadData(old => !old);
      });
      toast.success(i18n.t("flowbuilder.toasts.deleteSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async flowId => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId }).then(res => {
        setDeletingContact(null);
        setReloadData(old => !old);
      });
      toast.success(i18n.t("flowbuilder.toasts.duplicateSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const exportLink = (flow) => {
    history.push(`/flowbuilder/${flow.id}`);
  };

  const renderStatus = (active) => {
    return (
      <span className={`${classes.statusBadge} ${active ? classes.statusActive : classes.statusInactive}`}>
        <span
          className={classes.statusDot}
          style={{ background: active ? "#4caf50" : "#e53935" }}
        />
        {active ? i18n.t("flowbuilder.status.active") : i18n.t("flowbuilder.status.inactive")}
      </span>
    );
  };

  return (
    <div className={classes.container}>
      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData(old => !old)}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={e =>
          deletingContact ? handleDeleteWebhook(deletingContact.id) : () => {}
        }
      >
        {deletingContact
          ? i18n.t("flowbuilder.confirmationModal.deleteTitle")
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <ConfirmationModal
        title={
          deletingContact
            ? i18n.t("flowbuilder.confirmationModal.duplicateTitle", { flowName: deletingContact.name })
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={e =>
          deletingContact ? handleDuplicateFlow(deletingContact.id) : () => {}
        }
      >
        {deletingContact
          ? i18n.t("flowbuilder.confirmationModal.duplicateMessage")
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Route size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("flowbuilder.title")}
              <span className={classes.countChip}>{filteredWebhooks.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("flowbuilder.subtitle")}
            </div>
          </div>
        </div>

        <Button className={classes.newBtn} onClick={handleOpenContactModal}>
          <Plus size={16} strokeWidth={2} />
          {i18n.t("flowbuilder.buttons.add")}
        </Button>
      </div>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        elevation={0}
      >
        <div className={classes.tableScroll}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="left">{i18n.t("contacts.table.name")}</TableCell>
                <TableCell align="center" className={classes.statusCell}>{i18n.t("flowbuilder.table.status")}</TableCell>
                <TableCell align="right" className={classes.actionsCell}>{i18n.t("contacts.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && webhooks.length === 0 ? (
                <TableRowSkeleton columns={3} />
              ) : filteredWebhooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className={classes.emptyState}>
                    {ticketSearchParam
                      ? "Nenhum fluxo corresponde à busca."
                      : "Nenhum fluxo cadastrado."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredWebhooks.map((flow) => (
                  <TableRow
                    key={flow.id}
                    className={classes.tableRow}
                    onClick={() => history.push(`/flowbuilder/${flow.id}`)}
                  >
                    <TableCell align="left" className={classes.nameCell}>
                      <span className={classes.flowName}>
                        <Route size={16} strokeWidth={1.8} />
                        {flow.name || "-"}
                      </span>
                    </TableCell>
                    <TableCell align="center" className={classes.statusCell}>{renderStatus(flow.active)}</TableCell>
                    <TableCell align="right" className={classes.actionsCell} onClick={(e) => e.stopPropagation()}>
                      <AppTooltip title={i18n.t("flowbuilder.buttons.editName")}>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => {
                            setDeletingContact(flow);
                            setSelectedContactId(flow.id);
                            setSelectedWebhookName(flow.name);
                            setContactModalOpen(true);
                          }}
                        >
                          <Pencil size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                      <AppTooltip title={i18n.t("flowbuilder.buttons.editFlow")}>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => exportLink(flow)}
                        >
                          <Wrench size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                      <AppTooltip title={i18n.t("flowbuilder.buttons.duplicate")}>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => {
                            setDeletingContact(flow);
                            setConfirmDuplicateOpen(true);
                          }}
                        >
                          <Copy size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                      <AppTooltip title={i18n.t("flowbuilder.buttons.delete")}>
                        <IconButton
                          size="small"
                          className={classes.deleteActionBtn}
                          onClick={() => {
                            setDeletingContact(flow);
                            setConfirmOpen(true);
                          }}
                        >
                          <Trash2 size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {loading && webhooks.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </div>
  );
};

export default FlowBuilder;
